import { supabase } from '../lib/supabase';

class BadgeService {
  // Get all badges with user assignment counts
  async getBadges(options = {}) {
    try {
      let query = supabase?.from('badges')?.select(`
          id,
          title,
          description,
          category,
          points,
          image_url,
          is_active,
          criteria,
          created_at,
          updated_at,
          user_badges!badge_id(count)
        `)?.order('created_at', { ascending: false });

      // Filter by active status if specified
      if (options?.activeOnly !== undefined) {
        query = query?.eq('is_active', options?.activeOnly);
      }

      // Filter by category if specified
      if (options?.category && options?.category !== 'all') {
        query = query?.eq('category', options?.category);
      }

      // Add search functionality
      if (options?.search?.trim()) {
        query = query?.or(`title.ilike.%${options?.search?.trim()}%,description.ilike.%${options?.search?.trim()}%`);
      }

      // Add limit if specified
      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching badges:', error);
        return { data: null, error: error?.message };
      }

      // Transform data to include assignment counts
      const badgesWithCounts = data?.map(badge => ({
        ...badge,
        assignment_count: badge?.user_badges?.[0]?.count || 0
      })) || [];

      return { data: badgesWithCounts, error: null };
    } catch (error) {
      console.error('Failed to fetch badges:', error);
      return { data: null, error: 'Failed to fetch badges' };
    }
  }

  // Get a specific badge by ID
  async getBadgeById(id) {
    try {
      const { data, error } = await supabase?.from('badges')?.select(`
          id,
          title,
          description,
          category,
          points,
          image_url,
          is_active,
          criteria,
          created_at,
          updated_at,
          user_badges!badge_id(count)
        `)?.eq('id', id)?.single();

      if (error) {
        console.error('Error fetching badge:', error);
        return { data: null, error: error?.message };
      }

      // Add assignment count
      const badgeWithCount = {
        ...data,
        assignment_count: data?.user_badges?.[0]?.count || 0
      };

      return { data: badgeWithCount, error: null };
    } catch (error) {
      console.error('Failed to fetch badge:', error);
      return { data: null, error: 'Failed to fetch badge' };
    }
  }

  // Create a new badge
  async createBadge(badgeData) {
    try {
      const { data, error } = await supabase?.from('badges')?.insert([{
          title: badgeData?.title?.trim(),
          description: badgeData?.description?.trim(),
          category: badgeData?.category,
          points: badgeData?.points || 10,
          image_url: badgeData?.image_url?.trim() || null,
          is_active: badgeData?.is_active !== undefined ? badgeData?.is_active : true,
          criteria: badgeData?.criteria || {}
        }])?.select()?.single();

      if (error) {
        console.error('Error creating badge:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Failed to create badge:', error);
      return { data: null, error: 'Failed to create badge' };
    }
  }

  // Update an existing badge
  async updateBadge(id, badgeData) {
    try {
      const updateData = {
        title: badgeData?.title?.trim(),
        description: badgeData?.description?.trim(),
        category: badgeData?.category,
        points: badgeData?.points,
        image_url: badgeData?.image_url?.trim() || null,
        is_active: badgeData?.is_active,
        criteria: badgeData?.criteria || {},
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('badges')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) {
        console.error('Error updating badge:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Failed to update badge:', error);
      return { data: null, error: 'Failed to update badge' };
    }
  }

  // Delete a badge
  async deleteBadge(id) {
    try {
      // First check if badge has any user assignments
      const { data: assignments, error: checkError } = await supabase?.from('user_badges')?.select('id')?.eq('badge_id', id)?.limit(1);

      if (checkError) {
        console.error('Error checking badge assignments:', checkError);
        return { data: null, error: checkError?.message };
      }

      if (assignments?.length > 0) {
        return { 
          data: null, 
          error: 'Cannot delete badge that has been assigned to users. Consider deactivating it instead.' 
        };
      }

      const { data, error } = await supabase?.from('badges')?.delete()?.eq('id', id)?.select()?.single();

      if (error) {
        console.error('Error deleting badge:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Failed to delete badge:', error);
      return { data: null, error: 'Failed to delete badge' };
    }
  }

  // Get badge statistics
  async getBadgeStatistics() {
    try {
      const { data: badges, error: badgeError } = await supabase?.from('badges')?.select('id, category, is_active');

      if (badgeError) {
        console.error('Error fetching badge statistics:', badgeError);
        return { data: null, error: badgeError?.message };
      }

      const { data: userBadges, error: userBadgeError } = await supabase?.from('user_badges')?.select('badge_id, status');

      if (userBadgeError) {
        console.error('Error fetching user badge statistics:', userBadgeError);
        return { data: null, error: userBadgeError?.message };
      }

      // Calculate statistics
      const stats = {
        total_badges: badges?.length || 0,
        active_badges: badges?.filter(b => b?.is_active)?.length || 0,
        inactive_badges: badges?.filter(b => !b?.is_active)?.length || 0,
        by_category: {},
        total_assignments: userBadges?.length || 0,
        earned_badges: userBadges?.filter(ub => ub?.status === 'earned')?.length || 0,
        in_progress_badges: userBadges?.filter(ub => ub?.status === 'in_progress')?.length || 0
      };

      // Group by category
      badges?.forEach(badge => {
        if (!stats?.by_category?.[badge?.category]) {
          stats.by_category[badge.category] = 0;
        }
        stats.by_category[badge.category]++;
      });

      return { data: stats, error: null };
    } catch (error) {
      console.error('Failed to fetch badge statistics:', error);
      return { data: null, error: 'Failed to fetch badge statistics' };
    }
  }

  // Search badges with fuzzy matching (if available)
  async searchBadges(searchTerm, options = {}) {
    try {
      if (!searchTerm?.trim()) {
        return this.getBadges(options);
      }

      let query = supabase?.from('badges')?.select(`
          id,
          title,
          description,
          category,
          points,
          image_url,
          is_active,
          criteria,
          created_at,
          updated_at,
          user_badges!badge_id(count)
        `)?.or(`title.ilike.%${searchTerm?.trim()}%,description.ilike.%${searchTerm?.trim()}%`)?.order('created_at', { ascending: false });

      // Apply filters
      if (options?.activeOnly !== undefined) {
        query = query?.eq('is_active', options?.activeOnly);
      }

      if (options?.category && options?.category !== 'all') {
        query = query?.eq('category', options?.category);
      }

      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error searching badges:', error);
        return { data: null, error: error?.message };
      }

      // Transform data to include assignment counts
      const badgesWithCounts = data?.map(badge => ({
        ...badge,
        assignment_count: badge?.user_badges?.[0]?.count || 0
      })) || [];

      return { data: badgesWithCounts, error: null };
    } catch (error) {
      console.error('Failed to search badges:', error);
      return { data: null, error: 'Failed to search badges' };
    }
  }

  // Get user assignments for a specific badge
  async getBadgeAssignments(badgeId, options = {}) {
    try {
      let query = supabase?.from('user_badges')?.select(`
          id,
          status,
          progress,
          earned_at,
          created_at,
          updated_at,
          user_profiles!user_id(
            id,
            display_name,
            email
          )
        `)?.eq('badge_id', badgeId)?.order('created_at', { ascending: false });

      if (options?.status) {
        query = query?.eq('status', options?.status);
      }

      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching badge assignments:', error);
        return { data: null, error: error?.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Failed to fetch badge assignments:', error);
      return { data: null, error: 'Failed to fetch badge assignments' };
    }
  }

  // Toggle badge active status
  async toggleBadgeStatus(id) {
    try {
      // First get the current status
      const { data: currentBadge, error: fetchError } = await supabase?.from('badges')?.select('is_active')?.eq('id', id)?.single();

      if (fetchError) {
        console.error('Error fetching badge status:', fetchError);
        return { data: null, error: fetchError?.message };
      }

      // Toggle the status
      const { data, error } = await supabase?.from('badges')?.update({ 
          is_active: !currentBadge?.is_active,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) {
        console.error('Error toggling badge status:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Failed to toggle badge status:', error);
      return { data: null, error: 'Failed to toggle badge status' };
    }
  }
}

export default new BadgeService();